import React, {useState, useRef } from "react"
import styles from "./playercontrols.module.scss"
import ReactPlayer from "react-player"
import Slider from '@mui/material/Slider';
import { Box } from "@mui/system";
import { Fab } from "@mui/material";
import { saveAs } from 'file-saver';
import { logEvent } from "analyticsEvents";


interface Props {
  songUrl: string,
  songId: string,
  songTitle: string
}

const PlayerControls = (props: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const player = useRef<ReactPlayer>(null)

  return (
    <div className={styles.playerControlsRoot}>
      <Slider
        size="small"
        min={0}
        max={duration}
        defaultValue={0}
        onChange={(_, value) => {
          player.current?.seekTo(value as number);
          logEvent('song_seeked', {
            song_id: props.songId,
            seek_position: value,
            duration: duration
          });
        }}
        value={progress}
        sx={{
          color: 'rgba(220,220,220,0.87)',
          height: 4,
          '& .MuiSlider-thumb': {
            width: 8,
            height: 8,
            transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
            '&:before': {
              boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
            },
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0px 0px 0px 8px ${
                  'rgb(0 0 0 / 16%)'
              }`,
            },
            '&.Mui-active': {
              width: 20,
              height: 20,
            },
          },
          '& .MuiSlider-rail': {
            opacity: 0.28,
          },
          '& .MuiSlider-track': {
            background: 'linear-gradient(90deg, #44F976 -0.12%, #FE9A43 19.16%, #F43980 46.79%, #833CE3 74.42%, #3D96E9 99.96%)',
          }
        }}
      />
      <Box display='flex' flexDirection='row' justifyContent="space-between">
        <span className={styles.progressDurationText}>{formatSec(progress)}</span>
        <span className={styles.progressDurationText}>{formatSec(duration)}</span>
      </Box>
      <Box className={styles.buttonContainer} display='flex' flexDirection='row' justifyContent="space-evenly" alignItems="center">
        <Fab
          sx={{
            backgroundColor: "transparent",
            border: "1px #F1F1F1 solid",
            boxShadow: "none",
            height: 40,
            width: 40
          }}
          href="https://link.mayk.it/rdYp/songDownloadLink"
          target="_blank"
          onClick={() => {
            logEvent('song_like_clicked', {
              song_id: props.songId
            });
          }}
        >
          <img
            className={styles.smallButtonIcon}
            src={require("../../resources/fire.svg").default}
            />
        </Fab>
        <Fab
          className={styles.playPauseButton}
          sx={{
            backgroundColor: "black",
            '&:hover': {
              backgroundColor: "#505050"
            }
          }}
          onClick={() => {
            logEvent('song_play_toggled', {
              song_id: props.songId,
              newPlaybackState: !isPlaying
            });
            setIsPlaying(!isPlaying);
          }}
        >
          <img
            className={styles.playPauseIcon}
            src={isPlaying ? require("../../resources/pause.svg").default : require("../../resources/play.svg").default}
            />
        </Fab>
        <Fab
          sx={{
            backgroundColor: "transparent",
            border: "1px #F1F1F1 solid",
            boxShadow: "none",
            height: 40,
            width: 40
          }}
          onClick={() => {
            saveAs(props.songUrl, `${props.songTitle}.m4a`);
            logEvent('song_downloaded', {
              song_id: props.songId
            });
          }}
        >
          <img
            className={styles.smallButtonIcon}
            src={require("../../resources/download.svg").default}
            />
        </Fab>
      </Box>
      <ReactPlayer
        hidden
        ref={player}
        url={props.songUrl}
        playing={isPlaying}
        onDuration={setDuration}
        progressInterval={100}
        onProgress={(progressData) => setProgress(progressData.playedSeconds)}
        config={{
          file: {
            forceAudio: true,
          },
        }}
      />
    </div>
  )
}

const formatSec = (durationSeconds: number) => {
  const zeroPad = (num: number) => num >= 10 ? `${num}` : `0${num}`; 
  const minutes = Math.floor(durationSeconds / 60)
  const seconds = Math.round(durationSeconds % 60)
  return `${zeroPad(minutes)}:${zeroPad(seconds)}`;
}

export default PlayerControls
